/**
 * /pricing — Page offre SmartLawyer
 * Payant = violet / Navigation = bleu
 */

import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Trans, useTranslation } from 'react-i18next';
import { getDateLocale } from '@/i18n';
import { PublicHeader, PublicFooter } from '@/components/jurisprudence';
import { Breadcrumb } from '@/components/jurisprudence/Breadcrumb';
import { usePublicAuth } from '@/hooks/usePublicAuth';
import { useStats } from '@/hooks/useStats';

const CHECKOUT_BASE = '/api/checkout';

async function createCheckoutSession(token: string, interval: 'month' | 'year') {
  const res = await fetch(`${CHECKOUT_BASE}/session`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
    body: JSON.stringify({ interval }),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.detail || `HTTP ${res.status}`);
  }
  return res.json();
}

// Textes : pricing.free.features.<id>, pricing.pro.features.<id>, pricing.faq.items.<id>
const FREE_FEATURES = ['fiches', 'search', 'public', 'summary'];

const PRO_FEATURES = ['unlimited', 'graph', 'mcp', 'fiches', 'filters', 'export', 'support'];

const FAQS = ['cancel', 'trial', 'mcp', 'secure'];

export default function PublicPricing() {
  const { t } = useTranslation();
  const { user, getToken, subStatus }  = usePublicAuth();
  const navigate                       = useNavigate();
  const [interval, setInterval]        = useState<'month' | 'year'>('month');
  const [loading, setLoading]          = useState(false);
  const [error, setError]              = useState('');
  const [openFaq, setOpenFaq]          = useState<number | null>(null);
  const { totalStr, totalShort }       = useStats();
  const isPaid = subStatus?.plan === 'paid' && subStatus?.has_subscription;

  const handleSubscribe = async () => {
    setError('');
    if (!user) {
      navigate(`/login?intent=subscribe&redirect=/settings/subscription`);
      return;
    }
    setLoading(true);
    try {
      const token = await getToken();
      if (!token) throw new Error(t('pricing.session_expired'));
      const data = await createCheckoutSession(token, interval);
      window.location.href = data.url;
    } catch (e: any) {
      setError(e.message);
      setLoading(false);
    }
  };

  const monthlyPrice  = 4.99;
  const yearlyPrice   = 49.99;
  const yearlyMonthly = yearlyPrice / 12;
  const fmtAmount = (n: number) =>
    new Intl.NumberFormat(getDateLocale(), { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n);
  const price = (n: number) => t('pricing.price', { amount: fmtAmount(n) });

  return (
    <>
      <Helmet>
        <title>{t('pricing.meta_title')}</title>
        <meta name="description" content={t('pricing.meta_description')} />
      </Helmet>

      <div className="min-h-screen bg-background flex flex-col">
        <PublicHeader />

        <Breadcrumb items={[
          { label: t('menu.home'), to: '/' },
          { label: t('nav.pricing') },
        ]} />

        <main className="flex-1">

          {/* ── Hero ── */}
          <section className="pt-16 pb-12 px-4 text-center">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-violet-50 dark:bg-violet-950/30 border border-violet-200 dark:border-violet-800 text-violet-600 dark:text-violet-400 text-xs font-semibold mb-6">
              <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 24 24">
                <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/>
              </svg>
              {t('pricing.hero.badge', { total: totalStr ?? t('pricing.default_total') })}
            </div>

            <h1 className="text-4xl sm:text-5xl font-bold tracking-tight mb-4 max-w-2xl mx-auto leading-tight">
              {t('subscription.title_start')}<br />
              <span className="text-violet-600 dark:text-violet-400">{t('subscription.title_highlight')}</span>
            </h1>
            <p className="text-lg text-muted-foreground max-w-xl mx-auto">
              {t('pricing.hero.desc')}
            </p>
          </section>

          {/* ── Toggle mensuel / annuel ── */}
          <div className="flex justify-center mb-10 px-4">
            <div className="flex items-center gap-1 p-1 rounded-xl bg-muted border">
              {(['month', 'year'] as const).map(iv => (
                <button
                  key={iv}
                  onClick={() => setInterval(iv)}
                  className={`px-5 py-2 rounded-lg text-sm font-semibold transition-all flex items-center gap-2 ${
                    interval === iv
                      ? 'bg-background shadow text-foreground'
                      : 'text-muted-foreground hover:text-foreground'
                  }`}
                >
                  {iv === 'month' ? t('pricing.monthly') : t('pricing.yearly')}
                  {iv === 'year' && (
                    <span className="text-[11px] bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-400 px-1.5 py-0.5 rounded-md font-bold">
                      −16%
                    </span>
                  )}
                </button>
              ))}
            </div>
          </div>

          {/* ── Plans ── */}
          <section className="max-w-4xl mx-auto px-4 pb-16">
            {error && (
              <div className="mb-6 p-3 rounded-lg bg-destructive/10 border border-destructive/20 text-sm text-destructive text-center">
                {error}
              </div>
            )}

            <div className="grid sm:grid-cols-2 gap-6">

              {/* Plan Gratuit */}
              <div className="rounded-2xl border bg-card p-8 flex flex-col">
                <div className="mb-6">
                  <div className="flex items-center gap-2 mb-3">
                    <div className="text-xs font-bold uppercase tracking-widest text-muted-foreground">
                      {t('common.free')}
                    </div>
                    {user && !isPaid && (
                      <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-muted text-muted-foreground border">
                        {t('pricing.current_plan')}
                      </span>
                    )}
                  </div>
                  <div className="flex items-end gap-1 mb-1">
                    <span className="text-4xl font-bold">{t('pricing.price', { amount: '0' })}</span>
                  </div>
                  <p className="text-sm text-muted-foreground">{t('pricing.free.desc')}</p>
                </div>

                <ul className="space-y-3 mb-8 flex-1">
                  {FREE_FEATURES.map(f => (
                    <li key={f} className="flex items-start gap-2.5 text-sm">
                      <svg className="w-4 h-4 text-muted-foreground mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7"/>
                      </svg>
                      <span className="text-muted-foreground">{t(`pricing.free.features.${f}`)}</span>
                    </li>
                  ))}
                </ul>

                {!user || isPaid ? (
                  <Link
                    to="/login"
                    className="w-full flex items-center justify-center px-6 py-2.5 rounded-xl border-2 border-border hover:bg-accent text-sm font-semibold transition-colors"
                  >
                    {t('pricing.free.cta')}
                  </Link>
                ) : null}
              </div>

              {/* Plan Pro — violet */}
              <div className="rounded-2xl border-2 border-violet-600 dark:border-violet-500 bg-card p-8 flex flex-col relative shadow-xl shadow-violet-600/10">
                {/* Badge */}
                <div className="absolute -top-3.5 left-1/2 -translate-x-1/2">
                  <span className="bg-violet-600 text-white text-xs font-bold px-4 py-1 rounded-full shadow-lg">
                    {t('pricing.pro.recommended')}
                  </span>
                </div>

                <div className="mb-6">
                  <div className="flex items-center gap-2 mb-3">
                    <div className="text-xs font-bold uppercase tracking-widest text-violet-600 dark:text-violet-400">
                      Pro
                    </div>
                    {isPaid && (
                      <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-violet-100 dark:bg-violet-900/30 text-violet-600 dark:text-violet-400 border border-violet-300 dark:border-violet-700">
                        ✓ {t('pricing.current_plan')}
                      </span>
                    )}
                  </div>
                  <div className="flex items-end gap-1 mb-1">
                    {interval === 'month' ? (
                      <>
                        <span className="text-4xl font-bold">{price(monthlyPrice)}</span>
                        <span className="text-muted-foreground text-sm mb-1">{t('pricing.per_month')}</span>
                      </>
                    ) : (
                      <>
                        <span className="text-4xl font-bold">{price(yearlyMonthly)}</span>
                        <span className="text-muted-foreground text-sm mb-1">{t('pricing.per_month')}</span>
                      </>
                    )}
                  </div>
                  {interval === 'year' ? (
                    <p className="text-sm text-muted-foreground">
                      <Trans
                        i18nKey="pricing.pro.billed_yearly"
                        values={{ price: price(yearlyPrice), savings: price(monthlyPrice * 12 - yearlyPrice) }}
                        components={{ strong: <span className="font-semibold text-foreground" /> }}
                      />
                    </p>
                  ) : (
                    <p className="text-sm text-muted-foreground">{t('pricing.pro.cancel_anytime')}</p>
                  )}
                </div>

                <ul className="space-y-3 mb-8 flex-1">
                  {PRO_FEATURES.map(f => (
                    <li key={f} className="flex items-start gap-2.5 text-sm">
                      <svg className="w-4 h-4 text-violet-600 dark:text-violet-400 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7"/>
                      </svg>
                      <span>{t(`pricing.pro.features.${f}`, { total: totalStr ?? new Intl.NumberFormat(getDateLocale()).format(1000000) })}</span>
                    </li>
                  ))}
                </ul>

                {isPaid ? (
                  <Link
                    to="/settings/subscription"
                    className="w-full flex items-center justify-center gap-2 px-6 py-3 rounded-xl border-2 border-violet-600 text-violet-600 font-bold text-sm hover:bg-violet-50 dark:hover:bg-violet-950/20 transition-colors"
                  >
                    {t('pricing.manage')}
                  </Link>
                ) : (
                  <>
                    <button
                      onClick={handleSubscribe}
                      disabled={loading}
                      className="w-full flex items-center justify-center gap-2 px-6 py-3 rounded-xl bg-violet-600 text-white font-bold text-sm hover:bg-violet-700 disabled:opacity-50 transition-colors shadow-lg shadow-violet-600/25"
                    >
                      {loading ? (
                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      ) : (
                        <>
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                              d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z"/>
                          </svg>
                          {user ? (interval === 'year' ? t('pricing.pro.subscribe_yearly') : t('pricing.pro.subscribe_monthly')) : t('pricing.pro.start_subscribe')}
                        </>
                      )}
                    </button>
                    <p className="text-center text-xs text-muted-foreground mt-3">
                      {t('pricing.pro.secure_payment')}
                    </p>
                  </>
                )}
              </div>
            </div>

            {/* Social proof */}
            <div className="mt-10 flex flex-wrap justify-center gap-8 text-center">
              {[
                { id: 'decisions', value: totalShort ?? '1M+' },
                { id: 'price',     value: t('pricing.proof.price_value') },
                { id: 'french',    value: '100%' },
              ].map(({ id, value }) => (
                <div key={id}>
                  <p className="text-2xl font-bold text-violet-600 dark:text-violet-400">{value}</p>
                  <p className="text-xs text-muted-foreground mt-1">{t(`pricing.proof.${id}`)}</p>
                </div>
              ))}
            </div>
          </section>

          {/* ── FAQ ── */}
          <section className="max-w-2xl mx-auto px-4 pb-20">
            <h2 className="text-xl font-bold text-center mb-8">{t('pricing.faq.title')}</h2>
            <div className="space-y-2">
              {FAQS.map((faq, i) => (
                <div key={i} className="rounded-xl border bg-card overflow-hidden">
                  <button
                    onClick={() => setOpenFaq(openFaq === i ? null : i)}
                    className="w-full flex items-center justify-between px-5 py-4 text-left text-sm font-semibold hover:bg-accent transition-colors gap-3"
                  >
                    <span>{t(`pricing.faq.items.${faq}.q`)}</span>
                    <svg
                      className={`w-4 h-4 text-muted-foreground flex-shrink-0 transition-transform ${openFaq === i ? 'rotate-180' : ''}`}
                      fill="none" stroke="currentColor" viewBox="0 0 24 24"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7"/>
                    </svg>
                  </button>
                  {openFaq === i && (
                    <div className="px-5 pb-4 text-sm text-muted-foreground border-t pt-3">
                      {t(`pricing.faq.items.${faq}.a`)}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </section>

          {/* ── Footer CTA ── */}
          <section className="border-t bg-muted/30 px-4 py-16 text-center">
            {isPaid ? (
              <>
                <h2 className="text-2xl font-bold mb-3">{t('pricing.cta.paid_title')}</h2>
                <p className="text-muted-foreground mb-6 text-sm">
                  {t('pricing.cta.paid_desc')}
                </p>
                <Link
                  to="/settings/subscription"
                  className="inline-flex items-center gap-2 px-8 py-3 rounded-xl border-2 border-violet-600 text-violet-600 font-bold hover:bg-violet-50 dark:hover:bg-violet-950/20 transition-colors"
                >
                  {t('pricing.manage')}
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3"/>
                  </svg>
                </Link>
              </>
            ) : (
              <>
                <h2 className="text-2xl font-bold mb-3">{t('pricing.cta.title')}</h2>
                <p className="text-muted-foreground mb-6 text-sm">
                  {t('pricing.cta.desc')}
                </p>
                <button
                  onClick={handleSubscribe}
                  disabled={loading}
                  className="inline-flex items-center gap-2 px-8 py-3 rounded-xl bg-violet-600 text-white font-bold hover:bg-violet-700 disabled:opacity-50 transition-colors shadow-lg shadow-violet-600/20"
                >
                  {loading ? (
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  ) : (
                    <>
                      {t('pricing.cta.subscribe')}
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3"/>
                      </svg>
                    </>
                  )}
                </button>
              </>
            )}
          </section>

        </main>

        <PublicFooter />
      </div>
    </>
  );
}